import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Home, Plane, Users, User, Trophy, Clock, Flag, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// The three tabs answer three questions: "what are MY teams playing?",
// "what's on across MY year group?", "what's on across the whole club?".
// Away rows come from fa_fixtures (never bookings) so the calendar stays
// pitch-only — every row in Away has `origin: "away"`.
const TABS = [
  { key: "mine",       label: "My Fixtures",         icon: User,   path: "/fixtures/mine",        testid: "fixtures-tab-mine" },
  { key: "yearGroup",  label: "My Year Group",       icon: Users,  path: "/fixtures/year-group",  testid: "fixtures-tab-year-group" },
  { key: "club",       label: "Club Fixtures",       icon: Trophy, path: "/fixtures/club",        testid: "fixtures-tab-club" },
];
const LOCATIONS = [
  { key: "both", label: "All fixtures" },
  { key: "home", label: "Home only" },
  { key: "away", label: "Away only" },
];

const fmtKick = (t) => t || "TBC";
const dayLabel = (iso) => {
  try { return format(parseISO(iso), "EEE d MMM yyyy"); } catch { return iso; }
};

export default function Fixtures() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [location, setLocation] = useState("both");
  const [period, setPeriod] = useState("upcoming");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ count: 0 });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");

  useEffect(() => {
    const active = TABS.find(t => t.key === tab);
    if (!active) return;
    setLoading(true);
    api.get(active.path, { params: { location, period } })
      .then(({ data }) => {
        setRows(data.fixtures || []);
        setMeta({ count: data.count || 0, age_groups: data.age_groups });
      })
      .catch(err => toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load fixtures"))
      .finally(() => setLoading(false));
  }, [tab, location, period]);
  // Reset the venue pick when Home/Away changes — the option set is different.
  useEffect(() => { setVenueFilter("all"); }, [location]);

  const teamLabel = r => [r.age_group, r.team_name].filter(Boolean).join(" · ") || r.team_id;
  const venueKey = r => r.origin === "home" && r.pitch_name
    ? `${r.venue_short_code ? `[${r.venue_short_code}] ` : ""}${r.pitch_name}`
    : (r.venue || "");
  const teamOptions = useMemo(() => Array.from(new Map(rows.filter(r => r.team_id).map(r => [r.team_id, teamLabel(r)])).entries()).sort((a, b) => (a[1] || "").localeCompare(b[1] || "")), [rows]);
  // Venue options follow the Home/Away toggle: Home → our pitches ([code] pitch), Away → opposition venues, All → both.
  const venueOptions = useMemo(() => Array.from(new Set(rows.map(venueKey).filter(Boolean))).sort(), [rows]);
  const visibleRows = useMemo(() => rows.filter(r => {
    if (dateFrom && r.date && r.date < dateFrom) return false;
    if (dateTo && r.date && r.date > dateTo) return false;
    if (teamFilter !== "all" && r.team_id !== teamFilter) return false;
    if (venueFilter !== "all" && venueKey(r) !== venueFilter) return false;
    return true;
  }), [rows, dateFrom, dateTo, teamFilter, venueFilter]);
  const filtersActive = !!(dateFrom || dateTo || teamFilter !== "all" || venueFilter !== "all");
  const clearFilters = () => { setDateFrom(""); setDateTo(""); setTeamFilter("all"); setVenueFilter("all"); };

  // Group rows by date so the list reads like a calendar rather than a wall.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of visibleRows) {
      const k = r.date || "TBC";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    for (const [, list] of map) list.sort((a, b) => (a.kickoff || "00:00").localeCompare(b.kickoff || "00:00"));
    const asc = period === "upcoming";
    return [...map.entries()].sort(([a], [b]) => (a > b ? (asc ? 1 : -1) : a < b ? (asc ? -1 : 1) : 0));
  }, [visibleRows, period]);

  const homeCount = visibleRows.filter(r => r.origin === "home").length;
  const awayCount = visibleRows.filter(r => r.origin === "away").length;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10" data-testid="fixtures-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Fixtures</div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{period === "upcoming" ? "Games ahead" : "Past games"}</h1>
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="fixtures-period-toggle">
            {[["upcoming", "Upcoming"], ["past", "Past"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setPeriod(k)}
                data-testid={`fixtures-period-${k}`}
                className={`px-4 py-2 text-xs tracking-widest uppercase transition-colors ${period === k ? "bg-[#83B68B]/15 text-[#83B68B]" : "opacity-70 hover:opacity-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="opacity-70 text-sm mt-1">Home fixtures come from our pitch bookings. Away fixtures come from FA Full-Time (never allocated to a pitch, so they don't clutter the calendar).</p>
      </div>

      {/* Scope tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-2 mb-4" data-testid="fixtures-scope-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={t.testid}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs tracking-widest uppercase transition-colors ${
                active
                  ? "bg-[#83B68B]/15 text-[#83B68B] border border-[#83B68B]/50"
                  : "border border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Date / team / venue filters */}
      <div className="card-tactical rounded-md p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end" data-testid="fixtures-filters">
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5 inline-flex items-center gap-1"><Filter className="w-3 h-3" /> From</div>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="fixtures-date-from" />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">To</div>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="fixtures-date-to" />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">Team</div>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger data-testid="fixtures-team"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All teams</SelectItem>{teamOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name || id}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5 flex justify-between">{location === "home" ? "Pitch" : location === "away" ? "Away venue" : "Venue"} {filtersActive && <button type="button" onClick={clearFilters} data-testid="fixtures-clear-filters" className="underline opacity-70 hover:opacity-100 hover:text-red-400">Clear</button>}</div>
          <Select value={venueFilter} onValueChange={setVenueFilter}>
            <SelectTrigger data-testid="fixtures-venue"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{location === "home" ? "All home pitches" : location === "away" ? "All away venues" : "All venues"}</SelectItem>{venueOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Home/Away filter + counts */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="fixtures-location-filter">
          {LOCATIONS.map(l => (
            <button
              key={l.key}
              type="button"
              onClick={() => setLocation(l.key)}
              data-testid={`fixtures-loc-${l.key}`}
              className={`px-3 py-1.5 text-[11px] tracking-widest uppercase transition-colors ${
                location === l.key
                  ? "bg-[#83B68B] text-white"
                  : "hover:bg-muted"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] tracking-widest uppercase opacity-60" data-testid="fixtures-counts">
          {visibleRows.length}{filtersActive ? ` of ${meta.count}` : " total"} · {homeCount} home · {awayCount} away
        </div>
        {tab === "yearGroup" && meta.age_groups?.length > 0 && (
          <div className="text-[10px] tracking-widest uppercase opacity-60">
            · Age groups: {meta.age_groups.join(", ")}
          </div>
        )}
      </div>

      {loading ? (
        <div className="opacity-50 font-display tracking-widest text-sm" data-testid="fixtures-loading">LOADING…</div>
      ) : rows.length > 0 && visibleRows.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-70" data-testid="fixtures-filtered-empty">
          <div className="font-display font-bold text-lg">No fixtures match these filters</div>
          <button type="button" onClick={clearFilters} className="text-[10px] tracking-widest uppercase underline mt-2 opacity-80 hover:opacity-100">Clear filters</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-70" data-testid="fixtures-empty">
          <div className="font-display font-bold text-lg">No fixtures{location !== "both" ? ` (${location} only)` : ""}</div>
          <div className="text-xs opacity-70 mt-1">
            {tab === "mine" && "You aren't the owner or a coach on any team with a fixture yet."}
            {tab === "yearGroup" && "No fixtures on the horizon for your year group."}
            {tab === "club" && "No confirmed fixtures across the club yet."}
          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="fixtures-list">
          {grouped.map(([date, dayRows]) => (
            <div key={date} data-testid={`fixtures-day-${date}`}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-2">{dayLabel(date)}</div>
              <div className="space-y-2">
                {dayRows.map((r, i) => <FixtureCard key={`${r.origin}-${r.booking_id || r.fa_id}-${i}`} r={r} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FixtureCard({ r }) {
  const isAway = r.origin === "away";
  const cancelled = r.status_label === "cancelled";
  const changed = r.status_label === "changed";
  return (
    <div
      className={`card-tactical rounded-md p-3 md:p-4 border ${
        cancelled ? "border-red-500/50 bg-red-500/5" : changed ? "border-amber-500/50 bg-amber-500/5" : "border-border"
      }`}
      data-testid={`fixture-card-${isAway ? "away" : "home"}-${r.booking_id || r.fa_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded ${
                isAway ? "bg-blue-500/15 text-blue-300 border border-blue-500/40" : "bg-[#83B68B]/15 text-[#83B68B] border border-[#83B68B]/40"
              }`}
            >
              {isAway ? <><Plane className="w-3 h-3" /> Away</> : <><Home className="w-3 h-3" /> Home</>}
            </span>
            {cancelled && <span className="text-[10px] tracking-widest uppercase text-red-400 px-2 py-0.5 rounded border border-red-500/40">Cancelled by FA</span>}
            {changed && <span className="text-[10px] tracking-widest uppercase text-amber-300 px-2 py-0.5 rounded border border-amber-500/40">Moved</span>}
            {r.match_type && <span className="text-[10px] tracking-widest uppercase opacity-60">{r.match_type}</span>}
          </div>
          <div className="font-display font-bold text-base md:text-lg mt-1 truncate">
            {r.team_name || "—"}{r.opponent ? ` vs ${r.opponent}` : ""}
          </div>
          <div className="text-xs opacity-70 mt-0.5 inline-flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtKick(r.kickoff)}</span>
            {(r.venue || r.pitch_name) && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {r.origin === "home" && r.pitch_name ? (
                  <>
                    {r.venue_short_code && <span className="text-[#83B68B] font-semibold" data-testid={`fixture-venue-code-${r.id || r.booking_id || ""}`}>[{r.venue_short_code}]</span>}
                    <span>{r.pitch_name}</span>
                    {r.venue_name && <span className="opacity-60">· {r.venue_name}</span>}
                  </>
                ) : (
                  <span>{r.venue || r.pitch_name || "Venue TBC"}</span>
                )}
              </span>
            )}
            {r.age_group && <span>{r.age_group}</span>}
            {r.referee_name && !isAway && <span className="inline-flex items-center gap-1"><Flag className="w-3 h-3" /> {r.referee_name}</span>}
          </div>
          {changed && r.previous_snapshot && (
            <div className="text-[11px] opacity-70 mt-1">
              Previously: {r.previous_snapshot.date} · {r.previous_snapshot.kickoff}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
